<template>
    <IdenfySection />

    <div class="pt-6 flex flex-col gap-y-6">
        <div
            v-if="verificationUserStore.billingTypeIsBusiness"
            class="flex flex-col gap-y-3"
        >
            <div class="text-sm text-gray-370 dark:text-white font-medium">
                Upload Certificate of Formation & EIN
            </div>

            <UploadFile
                v-model:files="certificatesFiles"
                title="Select File or Drag & Drop"
                fileSizeText="recommend size 4MB"
                :maxSize="MAX_FILE_SIZE_MB"
                :truncateLength="truncateLength"
            />
        </div>

        <div class="flex flex-col gap-y-3">
            <div class="text-sm text-gray-370 dark:text-white font-medium">
                Upload Proof of {{ verificationUserStore.billingTypeIsBusiness ? 'Business' : '' }} Address
            </div>

            <UploadFile
                v-model:files="businessAddressFiles"
                title="Select File or Drag & Drop"
                fileSizeText="recommend size 4MB"
                :maxSize="MAX_FILE_SIZE_MB"
                :truncateLength="truncateLength"
            />
        </div>
    </div>

    <div class="w-full mt-7 pt-4 flex justify-end gap-x-5 border-t border-gray-100 dark:border-blue-720">
        <PrimaryBtn
            class="max-mob:w-full"
            borderOutlinedStyle="border-gray-370 dark:border-gray-100"
            textOutlinedStyle="text-gray-370 dark:text-gray-100"
            outlined
            @click="prevStep"
        >
            Go Back
        </PrimaryBtn>

        <PrimaryBtn
            class="max-mob:w-full"
            :loading="verificationUserStore.actionLoading"
            :disabled="disableNextStep || disableReviewAction"
            @click="submitForm"
        >
            Next
        </PrimaryBtn>
    </div>
</template>

<script setup>
import { computed, inject } from 'vue'

import IdenfySection from '@/views/verification/sections/IdenfySection.vue'
import UploadFile from '@/components/uploadFile/UploadFile.vue'
import PrimaryBtn from '@/components/buttons/PrimaryBtn.vue'

import { useVerificationUserStore } from '@/store/call-tracking/verificationUserStore'

import { getMbFromBytes } from '@/helpers/app-helper'

import { VERIFICATION_STEPS, STATUSES } from '@/constants/verification'
import { viewPorts } from '@/constants/viewPorts'

const emits = defineEmits(['nextStep', 'prevStep', 'goToReview'])

const currentViewSize = inject('currentViewSize')

const verificationUserStore = useVerificationUserStore()

const MAX_FILE_SIZE_MB = 4

const isMobView = computed(() => viewPorts.mob >= currentViewSize.value)

const truncateLength = computed(() => (isMobView.value ? 30 : 50))

const certificatesFiles = computed({
    get() {
        return verificationUserStore.kyc_files.certificates
    },
    set(value) {
        verificationUserStore.updateKycFiles('certificates', value)
    },
})

const businessAddressFiles = computed({
    get() {
        return verificationUserStore.kyc_files.business_address
    },
    set(value) {
        verificationUserStore.updateKycFiles('business_address', value)
    },
})

const certificatesDone = computed(() => verificationUserStore.verificationSteps?.kycStep?.certificatesDone)
const addressesDone = computed(() => verificationUserStore.verificationSteps?.kycStep?.addressesDone)

const hasOversizedCertificatesFiles = computed(() => certificatesFiles.value.some((file) => getMbFromBytes(file.size) > MAX_FILE_SIZE_MB))
const hasOversizedBusinessAddressFiles = computed(() => businessAddressFiles.value.some((file) => getMbFromBytes(file.size) > MAX_FILE_SIZE_MB))

const filesHaveBeenAdded = computed(() => (!verificationUserStore.billingTypeIsBusiness || certificatesDone.value || certificatesFiles.value.length > 0)
    && (addressesDone.value || businessAddressFiles.value.length > 0))

const someFilesHaveBeenAdded = computed(() => (verificationUserStore.billingTypeIsBusiness && certificatesFiles.value.length > 0) || businessAddressFiles.value.length > 0)

const disableNextStep = computed(() => hasOversizedCertificatesFiles.value || hasOversizedBusinessAddressFiles.value || !filesHaveBeenAdded.value || verificationUserStore.actionLoading || !verificationUserStore.idenfyIsVerify)
const disableReviewAction = computed(() => hasOversizedCertificatesFiles.value || hasOversizedBusinessAddressFiles.value || (!someFilesHaveBeenAdded.value && verificationUserStore.kycStepStatus === STATUSES.rejected.id))

async function submitForm() {
    if (disableNextStep.value || disableReviewAction.value) {
        return
    }

    if (verificationUserStore.kycStepDone && (!verificationUserStore.billingTypeIsBusiness || !certificatesFiles.value.length) && !businessAddressFiles.value.length) {
        nextStep()

        return
    }

    if (!verificationUserStore.billingTypeIsBusiness) {
        verificationUserStore.updateKycFiles('certificates', [])
    }

    const { success } = await verificationUserStore.updateVerificationDataKYC()

    if (success) {
        nextStep()
    }
}

function nextStep() {
    emits('nextStep', VERIFICATION_STEPS.payment_method.id)
}

function prevStep() {
    emits('prevStep')
}
</script>
